import { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";

const SketchComponent = ({ idPrefix }) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [items, setItems] = useState([]);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerSizeRef = useRef({ width: 0, height: 0 });

  useEffect(() => {
    const width = containerRef.current.offsetWidth;
    const height = containerRef.current.offsetHeight;
    containerSizeRef.current = { width, height };

    const initialPositions = Array.from({ length: 5 }, (_, index) => {
      const x = Math.random() * (width - 12);
      const y = Math.random() * (height - 12);
      const dx = Math.random() * 2 - 1;
      const dy = Math.random() * 2 - 1;
      return { id: `${idPrefix}-item${index + 1}`, position: { x, y }, velocity: { dx, dy } };
    });

    setItems(initialPositions);
  }, [idPrefix]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const newWidth = entry.contentRect.width;
        const newHeight = entry.contentRect.height;
        const prevWidth = containerSizeRef.current.width;
        const prevHeight = containerSizeRef.current.height;

        if (prevWidth === 0 || prevHeight === 0) {
          containerSizeRef.current = { width: newWidth, height: newHeight };
          return;
        }

        const xRatio = newWidth / prevWidth;
        const yRatio = newHeight / prevHeight;

        setItems(prevItems => {
          return prevItems.map(item => {
            let x = item.position.x * xRatio;
            let y = item.position.y * yRatio;

            x = Math.max(0, Math.min(newWidth - 12, x));
            y = Math.max(0, Math.min(newHeight - 12, y));

            return {
              ...item,
              position: { x, y },
            };
          });
        });
        containerSizeRef.current = { width: newWidth, height: newHeight };
      }
    });

    const node = containerRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  useEffect(() => {
    if (isDragging) return;

    const interval = setInterval(() => {
      setItems(prevItems => {
        return prevItems.map(item => {
          let { x, y } = item.position;
          let { dx, dy } = item.velocity;
          const containerWidth = containerRef.current.offsetWidth;
          const containerHeight = containerRef.current.offsetHeight;

          x += dx;
          y += dy;

          if (x <= 0 || x >= containerWidth - 12) dx = -dx;
          if (y <= 0 || y >= containerHeight - 12) dy = -dy;

          x = Math.max(0, Math.min(containerWidth - 12, x));
          y = Math.max(0, Math.min(containerHeight - 12, y));

          return {
            ...item,
            position: { x, y },
            velocity: { dx, dy },
          };
        });
      });
    }, 16);

    return () => clearInterval(interval);
  }, [isDragging]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragStop = (e, ui, id) => {
    setIsDragging(false);
    const newPosition = { x: ui.x, y: ui.y };
    setItems(prevItems => prevItems.map(item => (item.id === id ? { ...item, position: newPosition } : item)));
  };

  const handleDrag = (e, ui, id) => {
    const newPosition = { x: ui.x, y: ui.y };
    setItems(prevItems => prevItems.map(item => (item.id === id ? { ...item, position: newPosition } : item)));
    drawLines();
  };

  const handleMouseMove = e => {
    const rect = containerRef.current.getBoundingClientRect();
    setCursorPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseEnter = () => {
    setCursorVisible(true);
  };

  const handleMouseLeave = () => {
    setCursorVisible(false);
  };

  const drawLines = () => {
    const svg = svgRef.current;
    if (!svg) return;

    svg.innerHTML = "";

    items.forEach((item, index) => {
      if (index < items.length - 1) {
        const nextItem = items[index + 1];
        const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
        line.setAttribute("x1", item.position.x + 6);
        line.setAttribute("y1", item.position.y + 6);
        line.setAttribute("x2", nextItem.position.x + 6);
        line.setAttribute("y2", nextItem.position.y + 6);
        line.setAttribute("stroke", "black");
        line.setAttribute("stroke-width", "0.5");
        svg.appendChild(line);
      }
    });
  };

  useEffect(() => {
    drawLines();
  });

  return (
    <div
      ref={containerRef}
      className="draggable-home-container"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <svg ref={svgRef} className="svg-overlay"></svg>
      {cursorVisible && (
        <div
          className="draggable-cursor-dot"
          style={{
            left: cursorPosition.x,
            top: cursorPosition.y,
          }}
        />
      )}
      {items.map(item => (
        <Draggable
          key={item.id}
          position={item.position}
          onStart={handleDragStart}
          onStop={(e, ui) => handleDragStop(e, ui, item.id)}
          onDrag={(e, ui) => handleDrag(e, ui, item.id)}
          bounds="parent"
        >
          <div id={item.id} className="draggable-item" style={{ width: 10, height: 10, backgroundColor: "black" }} />
        </Draggable>
      ))}
    </div>
  );
};

export default SketchComponent;
